// wetBrush.js (доработанный)
import chroma from 'chroma-js';
import { brushDefaults } from './helpers/brushLoader';
import { fillGradient } from './helpers/gradient';
import { getStrokeBounds, translatePoints, calculatePathDistances, calculatePath, interpolatePointsUniform } from './helpers/points';
import { interpolatePoints } from './helpers/pressure';

const defaultBrushSettings = brushDefaults.wet || {};

export async function drawWetStroke({
  color,
  gradientColor,
  lineWidth,
  points,
  time,
  sets = {},
}, context) {
  if (points.length === 0) return;

  const brushSettings = Object.assign({}, defaultBrushSettings, sets);
  const {
    opacity = 0.5,
    wobble = 0.05,
    density = 1.0,
    dryEdges = 1,
    fixedTaper = false,
    taperWidth = 0,
    taperLength = 2,
    textureStrength = 0.2,
    shape,
    // --- Новые параметры для плоской кисти ---
    flatEdges = false, // Включает наш функционал
    flatness = 2.0,
    autoRotation = true,
    angle = 0,
    edgeStep = 8, // Заимствовано из flatBrush для корректных углов
  } = brushSettings;

  const chromaColor = chroma(color);
  const originalAlpha = chromaColor.alpha();
  const bristleColor = gradientColor
    ? chroma('black').alpha(opacity / 2).hex()
    : chromaColor.alpha(opacity / 2).hex();

  // Предобработка точек
  const { points: prePoints, bounds } = preprocessPointsWet({
    points,
    lineWidth,
    wobble,
    flatEdges, // Передаём флаг
  });
  if (!bounds || prePoints.length === 0) return;

  // Длина пути
  const totalPathLength = calculatePath(prePoints);

  // Предвычисляем taper/углы
  const orientedPoints = precomputeTaperAndAnglesWet({
    points: prePoints,
    lineWidth,
    fixedTaper,
    taperLength,
    taperWidth,
    totalPathLength,
    // --- Параметры для плоской кисти ---
    flatEdges,
    autoRotation,
    angle,
    edgeStep,
  });

  // Временный холст
  const bufferCanvas = document.createElement('canvas');
  bufferCanvas.width = bounds.width;
  bufferCanvas.height = bounds.height;
  const bufferCtx = bufferCanvas.getContext('2d');

  // Параметры ворсинок
  let bristleCount = Math.round(400 * density);
  let bristleWidth = lineWidth / 30;
  let wobbleAmount = lineWidth * wobble;
  const randomnessFactor = 0.3;

  // Смещения ворсинок (круглые или эллиптические)
  const offsets = flatEdges
    ? generateEllipticOffsets(time, bristleCount, lineWidth, flatness)
    : generateBristleOffsets(time, lineWidth, bristleCount, randomnessFactor);

  bufferCtx.lineCap = 'round';
  bufferCtx.lineJoin = 'round';

  // Группировка по цвету и квантованной ширине
  const colorWidthGroups = groupOffsetsByColorAndWidth({
    offsets,
    chromaColor,
    opacity,
    time,
    textureStrength,
    baseBristleColor: gradientColor ? bristleColor : null,
    bristleWidth
  });

  // Рисуем подгруппы

  for (const key in colorWidthGroups) {
    const { color: groupColor, lineWidth: groupWidth, items } = colorWidthGroups[key];

    const path = new Path2D(); // общий путь с множеством subpath’ов
    bufferCtx.strokeStyle = groupColor;
    bufferCtx.lineWidth = groupWidth;

    const circles = [];

    for (const { offset } of items) {
      const res = appendBristleToPath({
        path,
        points: orientedPoints,
        lineWidth: groupWidth,
        mainLineWidth: lineWidth,
        color: groupColor,
        time,
        offset,
        wobbleAmount,
        dryEdges,
        taperWidth,
        flatEdges, // Передаём флаг
      });
      if (res?.drewCircles && res.drewCircles.length) circles.push(...res.drewCircles);
    }

    // Сначала fill кружков (если есть)
    if (circles.length) {
      bufferCtx.fillStyle = groupColor;
      bufferCtx.beginPath();
      for (const c of circles) {
        const r = Math.max(0.01, c.r || (groupWidth / 2));
        bufferCtx.moveTo(c.x + r, c.y);
        bufferCtx.arc(c.x, c.y, r, 0, Math.PI * 2);
      }
      bufferCtx.fill();
    }

    // Затем один stroke по накопленному пути
    bufferCtx.stroke(path);
  }

  if (gradientColor) {
    fillGradient(bufferCtx, orientedPoints, color, gradientColor, lineWidth);
    context.globalAlpha = 1;
  } else {
    context.globalAlpha = originalAlpha;
  }

  // Переносим буфер
  context.drawImage(
    bufferCanvas,
    0, 0, bounds.width, bounds.height,
    bounds.x, bounds.y, bounds.width, bounds.height
  );
  context.globalAlpha = 1;
}

// Псевдослучайный
function seededRandom(seed) {
  let x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}

// Углы пути (базовая версия для круглой кисти)
function addPathAngles(points) {
  if (!points || points.length === 0) return points;
  const out = new Array(points.length);
  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    let dx = 0, dy = 0;
    if (i > 0) {
      dx += p.x - points[i - 1].x;
      dy += p.y - points[i - 1].y;
    }
    if (i < points.length - 1) {
      dx += points[i + 1].x - p.x;
      dy += points[i + 1].y - p.y;
    }
    const angle = Math.atan2(dy, dx);
    out[i] = { ...p, angle };
  }
  return out;
}

// Углы пути (версия для плоской кисти со сглаживанием на концах)
function addPathAnglesWithEdgeSkipping(points, { edgeStep = 4, totalPathLength }) {
  if (!points || points.length === 0) return points;
  const n = points.length;
  const out = new Array(n);
  const clampIndex = (idx) => Math.max(0, Math.min(n - 1, idx));

  for (let i = 0; i < n; i++) {
    const p = points[i];
    const distanceFromEnd = totalPathLength - p.distance;
    const isInStartWindow = p.distance < distanceFromEnd && p.distance < 20;
    const isInEndWindow = p.distance > distanceFromEnd && distanceFromEnd < 20;
    let dx = 0, dy = 0;

    if (isInStartWindow) {
      const j = clampIndex(i + edgeStep);
      dx = points[j].x - p.x;
      dy = points[j].y - p.y;
    } else if (isInEndWindow) {
      const j = clampIndex(i - edgeStep);
      dx = p.x - points[j].x;
      dy = p.y - points[j].y;
    } else {
      const j = clampIndex(i + 1);
      dx = points[j].x - p.x;
      dy = points[j].y - p.y;
    }
    const angle = Math.atan2(dy, dx);
    out[i] = { ...p, angle };
  }
  return out;
}


// Круглые смещения (для wet)
function generateBristleOffsets(time, lineWidth, bristleCount, randomnessFactor) {
  const baseRadius = lineWidth * 0.5 * 0.9;
  let offsets = [];
  for (let i = 0; i < bristleCount; i++) {
    const angle = seededRandom(time + i) * Math.PI * 2;
    const distance = Math.sqrt(seededRandom(time + i + 100)) * baseRadius;
    const x = Math.cos(angle) * distance;
    const y = Math.sin(angle) * distance;
    const randX = (seededRandom(time + i + 200) - 0.5) * 2 * randomnessFactor * baseRadius;
    const randY = (seededRandom(time + i + 300) - 0.5) * 2 * randomnessFactor * baseRadius;

    const adjustedX = x + randX;
    const adjustedY = y + randY;
    const adjustedDistance = Math.sqrt(adjustedX ** 2 + adjustedY ** 2);

    offsets.push({ x: adjustedX, y: adjustedY, distance: adjustedDistance });
  }
  offsets.push({ x: 0, y: 0, distance: 0 }); // Центральная ворсинка
  const maxDistance = Math.max(...offsets.map(offset => offset.distance));
  offsets.forEach(offset => {
    offset.normalizedDistance = maxDistance > 0 ? offset.distance / maxDistance : 0;
  });
  offsets.sort((a, b) => a.distance - b.distance);
  return offsets;
}

// Эллиптические смещения (для flat)
function generateEllipticOffsets(time, count, radius, flatness) {
    const a = radius / 2;
    const b = radius / (2 + flatness * flatness);
    const arr = [];
    for (let i = 0; i < count; i++) {
        const r = Math.sqrt(seededRandom(time + i * 17.11));
        const theta = seededRandom(time + i * 9.73) * Math.PI * 2;
        const x = r * Math.cos(theta) * a;
        const y = r * Math.sin(theta) * b;
        const distance = Math.sqrt(x * x + y * y);
        const edge = Math.min(1, Math.sqrt((x * x) / (a * a) + (y * y) / (b * b)));
        arr.push({ x, y, distance, normalizedDistance: edge });
    }
    arr.sort((a, b) => a.distance - b.distance);
    return arr;
}


// Отрисовка одиночной точки (кружок)
function drawPointWet({
  points, lineWidth, color, offset, dryEdges, flatEdges
}, context) {
  const point = points[0];
  const pressure = point.pressure ?? 1;
  const averageTaper = point.taper ?? 1;

  if ((offset?.normalizedDistance || 0) < (1 - averageTaper) * dryEdges) {
      return { drewCircles: [] };
  }

  let effOffsetX = 0;
  let effOffsetY = 0;

  if (flatEdges) {
    const angle = point.angle ?? 0;
    const nx = Math.cos(angle + Math.PI / 2);
    const ny = Math.sin(angle + Math.PI / 2);
    const tx = Math.cos(angle);
    const ty = Math.sin(angle);
    effOffsetX = (nx * offset.x + tx * (offset.y || 0)) * averageTaper;
    effOffsetY = (ny * offset.x + ty * (offset.y || 0)) * averageTaper;
  } else {
    effOffsetX = offset.x * averageTaper;
    effOffsetY = offset.y * averageTaper;
  }

  const adjustedRadius = (lineWidth / 2) * pressure * averageTaper;

  return {
    drewCircles: [{
      x: point.x + effOffsetX,
      y: point.y + effOffsetY,
      r: adjustedRadius
    }]
  };
}

// Добавление ворсинки в общий Path2D
function appendBristleToPath(stroke) {
  const {
    path,
    points,
    offset = { x: 0, y: 0 },
    wobbleAmount,
    time,
    dryEdges,
    taperWidth,
    flatEdges,
  } = stroke;

  const strokeHasPressure = points[0]?.pressure != null;

  const { normalizedDistance = 0 } = offset;

  if (points.length === 1) {
    return drawPointWet(stroke);
  }

  let isDrawingSegment = false;
  const offsetSeed = offset.x * offset.y;

  for (let i = 1; i < points.length; i++) {
    const prevPoint = points[i - 1];
    const currPoint = points[i];

    const pressure = currPoint.pressure ?? 1;
    const averageTaper = (prevPoint.taper + currPoint.taper) / 2;

    const requiredMinDistance = (1 - currPoint.taper) * dryEdges;
    const rnd = seededRandom(normalizedDistance);
    const dev = (rnd - 0.5) * taperWidth;
    const randomizedDistance = normalizedDistance + dev;


    const drySkip = !strokeHasPressure && averageTaper < Math.min(1 - dryEdges, 0.1)

    const shouldSkip =
      randomizedDistance < requiredMinDistance ||
      drySkip ||
      pressure < (offset.pressureThreshold || 0);

    let effOffsetX = 0;
    let effOffsetY = 0;

    // Вычисляем смещение ворсинки
    if (flatEdges) {
        // Для плоской кисти поворачиваем смещение по нормали к пути
        const angle = currPoint.angle ?? prevPoint.angle ?? 0;
        const nx = Math.cos(angle + Math.PI / 2);
        const ny = Math.sin(angle + Math.PI / 2);
        const tx = Math.cos(angle);
        const ty = Math.sin(angle);
        // Применяем taper к повернутому смещению
        effOffsetX = (nx * offset.x + tx * (offset.y || 0)) * averageTaper;
        effOffsetY = (ny * offset.x + ty * (offset.y || 0)) * averageTaper;
    } else {
        // Для круглой кисти просто применяем taper
        effOffsetX = offset.x * averageTaper;
        effOffsetY = offset.y * averageTaper;
    }

    if (shouldSkip) {
      isDrawingSegment = false;
      continue;
    }

    const adjustedWobble = wobbleAmount * (1 - pressure * 0.5);
    const wobbleX = (seededRandom(time + offsetSeed + i + 200) - 0.5) * adjustedWobble;
    const wobbleY = (seededRandom(time + offsetSeed + i + 300) - 0.5) * adjustedWobble;

    const midX = (prevPoint.x + currPoint.x) / 2 + wobbleX + effOffsetX;
    const midY = (prevPoint.y + currPoint.y) / 2 + wobbleY + effOffsetY;
    const fromX = prevPoint.x + effOffsetX;
    const fromY = prevPoint.y + effOffsetY;

    if (!isDrawingSegment) {
      path.moveTo(fromX, fromY);
      isDrawingSegment = true;
    }
    path.quadraticCurveTo(fromX, fromY, midX, midY);
  }
  return { drewCircles: [] };
}

// Группировка по цвету и квантованной толщине
function groupOffsetsByColorAndWidth({
  offsets, chromaColor, opacity, time, textureStrength, baseBristleColor, bristleWidth
}) {
  const groups = {};
  for (let i = 0; i < offsets.length; i++) {
    const offset = offsets[i];
    let currentColor = baseBristleColor;
    if (!baseBristleColor) {
      const variation = seededRandom(time + offset.x + offset.y) * textureStrength * 4 - (textureStrength * 8 / 3);
      currentColor = chroma(chromaColor).brighten(variation).alpha(opacity / 2).hex();
    }
    const varyWidth = (w) => w * (0.8 + seededRandom(time + (offset.distance || 0)) * 0.4);
    const varied = varyWidth(bristleWidth);
    const quantStep = Math.max(0.2, bristleWidth * 0.2);
    const quantizedWidth = Math.max(0.1, Math.round(varied / quantStep) * quantStep);
    const key = `${currentColor}|w:${quantizedWidth.toFixed(2)}`;
    if (!groups[key]) groups[key] = { color: currentColor, lineWidth: quantizedWidth, items: [] };
    groups[key].items.push({ offset, varyWidth });
  }
  return groups;
}

// Предобработка точек (интерполяция)
function preprocessPointsWet({
  points,
  lineWidth,
  wobble,
  flatEdges,
}) {
  if (points.length === 0) return { points: [], bounds: null };
  const bounds = getStrokeBounds(points, lineWidth);
  if (!bounds) return { points: [], bounds: null };

  const offsetX = -bounds.x;
  const offsetY = -bounds.y;
  let pts = translatePoints(points, offsetX, offsetY);

  // Интерполяция: плоская кисть требует более равномерных точек
  if (flatEdges) {
      let uniformStep = Math.max(3, lineWidth / 5);
      if (pts[0]?.pressure) { uniformStep = 5; }
      pts = interpolatePointsUniform(pts, uniformStep);
  } else if (!pts[0]?.pressure) {
    if (wobble <= 0.1) {
      pts = interpolatePoints(pts, 0.3, 5);
    } else {
      pts = interpolatePoints(pts, 0.1);
    }
  }
  return { points: pts, bounds, offsetX, offsetY };
}

// Предвычисление taper и углов
function precomputeTaperAndAnglesWet({
  points,
  lineWidth,
  fixedTaper,
  taperLength,
  taperWidth,
  totalPathLength,
  flatEdges,
  autoRotation,
  angle,
  edgeStep
}) {
  const pathPoints = calculatePathDistances(points);

  // Сначала вычисляем taper, как и раньше
  const taperedPoints = pathPoints.map(point => {
    let basicTaper = 1;
    if (fixedTaper) {
      const fromBegining = point.distance;
      const fromEnd = totalPathLength - point.distance;
      const minDistance = Math.min(fromBegining, fromEnd);
      const effectiveDistance = Math.min(totalPathLength, lineWidth * taperLength);
      if (minDistance < effectiveDistance) {
        let t = minDistance / effectiveDistance;
        basicTaper = t * t * (3 - 2 * t); // easeInOutCubic
      }
    } else {
      basicTaper = Math.sin(point.pathRatio * Math.PI);
    }
    const pressureFactor = point.pressure ?? basicTaper;
    const taperStrength = 1 - taperWidth;
    const currentTaper = (1 - pressureFactor) * taperStrength;
    const taper = 1 - currentTaper;
    return {
      ...point,
      taper,
      angle: 0,
      pressure: point.pressure ?? 1,
    };
  });

  // Затем вычисляем углы в зависимости от режима кисти
  if (flatEdges) {
    if (autoRotation) {
      // Авто-поворот, используем улучшенный алгоритм
      return addPathAnglesWithEdgeSkipping(taperedPoints, { edgeStep, totalPathLength });
    } else {
      // Фиксированный угол
      const fixedAngleRad = angle * (Math.PI / 180);
      return taperedPoints.map(p => ({ ...p, angle: fixedAngleRad }));
    }
  } else {
    // Стандартные углы для круглой кисти
    return addPathAngles(taperedPoints);
  }
}